// Pure math helpers for the KonvaCanvas. Extracted out of the component so the
// fit / zoom / filename logic is testable without standing up a Konva stage.
// All functions are side-effect-free; the component composes them.

export interface Position {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface FitResult {
  scale: number;
  position: Position;
}

/**
 * Fit an image into a stage at the largest scale that preserves aspect ratio,
 * then center it. Returns the (scale, position) Konva needs for that fit.
 *
 * Returns scale=1 + position=(0,0) when either dimension is non-positive —
 * stage is mid-mount or the image hasn't loaded; the caller decides whether
 * to render at all.
 */
export function fitToStage(image: Size, stage: Size): FitResult {
  if (image.width <= 0 || image.height <= 0 || stage.width <= 0 || stage.height <= 0) {
    return { scale: 1, position: { x: 0, y: 0 } };
  }
  const scale = Math.min(stage.width / image.width, stage.height / image.height);
  return {
    scale,
    position: {
      x: (stage.width - image.width * scale) / 2,
      y: (stage.height - image.height * scale) / 2,
    },
  };
}

/**
 * Zoom centered on the *stage* center (not the cursor). Keeps the visible
 * stage center stable across zoom steps — the common UX for +/− buttons.
 *
 * For cursor-anchored zoom (mouse wheel pointing at a feature), pass the
 * cursor position as `anchor`.
 */
export function zoomFromCenter(
  currentScale: number,
  currentPosition: Position,
  factor: number,
  stage: Size,
  anchor: Position = { x: stage.width / 2, y: stage.height / 2 },
): { scale: number; position: Position } {
  const newScale = currentScale * factor;
  // dx/dy from the anchor to the current top-left, scaled by the zoom factor.
  return {
    scale: newScale,
    position: {
      x: anchor.x - (anchor.x - currentPosition.x) * factor,
      y: anchor.y - (anchor.y - currentPosition.y) * factor,
    },
  };
}

export function clampZoom(scale: number, min = 0.05, max = 20): number {
  if (Number.isNaN(scale)) return min;
  if (scale === Number.POSITIVE_INFINITY) return max;
  if (scale === Number.NEGATIVE_INFINITY) return min;
  return Math.min(Math.max(scale, min), max);
}

const KNOWN_EXTS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg'];

/**
 * Derive a download filename from a source URL. Falls back to `fallback` (with
 * a sensible default extension) when the URL is opaque (data: URI, blob URL,
 * remote with no extension).
 */
export function downloadFilenameFromUrl(
  url: string | null | undefined,
  fallback = 'image.png',
): string {
  if (!url) return fallback;
  try {
    // URL constructor needs an absolute URL; for relative paths fall back to
    // the parsed pathname instead.
    const pathname = url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')
      ? new URL(url).pathname
      : url;
    const lastSlash = pathname.lastIndexOf('/');
    const tail = lastSlash >= 0 ? pathname.slice(lastSlash + 1) : pathname;
    if (!tail) return fallback;
    const dot = tail.lastIndexOf('.');
    if (dot < 0) return `${tail}.${fallback.split('.').pop()}`;
    const ext = tail.slice(dot + 1).toLowerCase();
    return KNOWN_EXTS.includes(ext) ? tail : `${tail.slice(0, dot)}.${fallback.split('.').pop()}`;
  } catch {
    return fallback;
  }
}

/**
 * Wrap a Konva stage's data-URL into a download. Pure utility — the caller
 * provides the URL (e.g. `stage.toDataURL()`) and a filename; this just
 * creates the temporary <a> click. Returns a cleanup function for tests.
 */
export function triggerImageDownload(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
